import * as Sentry from "@sentry/react";
import { ASSOCIATED_TOKEN_PROGRAM_ID, TOKEN_PROGRAM_ID } from "@solana/spl-token";
import { PublicKey } from "@solana/web3.js";
import type { MinimalTokenInfo, MinimalTokenMap, MintInfoMap } from "@stepfinance/common";
import { FarmType } from "@stepfinance/farms";

import type { PoolInfo } from "../models/swap";
import { cache } from "./cache";
import { STEP_API_ENDPOINT } from "./swr";
import { deepClone } from "./utils";

type TokenSymbolMap = {
  [symbol: string]: string;
};

type TokenInfoState = {
  tokenMap: MinimalTokenMap;
  tokenSymbolMap: TokenSymbolMap;
};

const tokenInfo: TokenInfoState = {
  tokenMap: {},
  tokenSymbolMap: {},
};

export let tokenInfoReady = false;

export const load = async (network: string) => {
  try {
    const [mintMapResponse, tokenMapResponse] = await Promise.all([
      await fetch(`${STEP_API_ENDPOINT}/v1/markets/mints?cluster=${network}`),
      await fetch(`${STEP_API_ENDPOINT}/v1/markets/tokens?cluster=${network}`),
    ]);

    const tokenMapData: {
      tokenMap: MinimalTokenMap;
      tokenSymbolMap: TokenSymbolMap;
    } = await tokenMapResponse.json();

    const mintMapData: MintInfoMap = await mintMapResponse.json();

    tokenInfo.tokenMap = deepClone(tokenMapData.tokenMap);
    tokenInfo.tokenSymbolMap = deepClone(tokenMapData.tokenSymbolMap);
    tokenInfoReady = true;
    cache.addMintInfoMap(deepClone(mintMapData));
  } catch (e) {
    Sentry.captureException(e);
  }
};

export const addTokenDataToMap = (data: MinimalTokenInfo) => {
  tokenInfo.tokenMap[data.address] = data;
};

export const getTokenByMint = (mint: string): MinimalTokenInfo | undefined => {
  return tokenInfo.tokenMap[mint];
};

export const getTokenBySymbol = (symbol: string): MinimalTokenInfo | undefined => {
  const mint: string | undefined = tokenInfo.tokenSymbolMap[symbol];
  if (mint) {
    return tokenInfo.tokenMap[mint];
  }
};

export const getTokenName = (mint?: string | PublicKey, shorten = true): string => {
  const mintAddress = typeof mint === "string" ? mint : mint?.toBase58();

  if (!mintAddress) {
    return "N/A";
  }

  const knownSymbol = tokenInfo.tokenMap[mintAddress]?.symbol;
  if (knownSymbol) {
    return knownSymbol;
  }

  return shorten ? `${mintAddress.substring(0, 5)}...` : mintAddress;
};

export const getTokenIcon = (mintAddress?: string | PublicKey): string | undefined => {
  const address = typeof mintAddress === "string" ? mintAddress : mintAddress?.toBase58();
  if (!address) {
    return;
  }
  return tokenInfo.tokenMap[address]?.logoURI;
};

export const isKnownMint = (mintAddress: string, hideUnknownTokens: boolean) => {
  const token = tokenInfo.tokenMap[mintAddress];
  const isNotNFT = !token?.tags?.includes("nft");
  const isNotLPToken = !token?.tags?.includes("lp-token");
  const isNotSaberLPToken = !token?.tags?.includes("saber-stableswap-lp");

  if (hideUnknownTokens) {
    return !!token && isNotNFT && isNotLPToken && isNotSaberLPToken;
  }
  return isNotNFT && isNotLPToken && isNotSaberLPToken;
};

export const getPoolName = (pool: PoolInfo, shorten = true) => {
  const sorted = pool.pubkeys.holdingMints.map((a) => a.toBase58()).sort();
  return sorted.map((item) => getTokenName(item, shorten)).join("/");
};

export const getRewardPoolAssets = (rewardA: PublicKey, rewardB: PublicKey, type: FarmType) => {
  return type === FarmType.DUAL_REWARD ? `${getTokenName(rewardA)}/${getTokenName(rewardB)}` : getTokenName(rewardA);
};

export const getTokenMap = () => {
  return tokenInfo.tokenMap;
};

export const findAssociatedTokenAddressSync = (mint: PublicKey, owner: PublicKey): PublicKey => {
  return PublicKey.findProgramAddressSync(
    [owner.toBuffer(), TOKEN_PROGRAM_ID.toBuffer(), mint.toBuffer()],
    ASSOCIATED_TOKEN_PROGRAM_ID,
  )[0];
};

//removing extra quotes from local storage because saber is craycray
const localStorageNetwork = localStorage.getItem("network")?.replaceAll(`"`, "");
void load(localStorageNetwork || "mainnet-beta");
